import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

export const getTodayDateString = () => {
  const today = new Date()
  return today.toISOString().slice(0, 10)
}

export function removeSpacesAfterComma(str: string): string {
  if (typeof str === 'string') {
    return str.replace(/,\s+/g, ',')
  } else {
    return ''
  }
}

export function processSearchString(searchStr: string): string {
  return (searchStr || '').toLowerCase().trim()
}

export const toProperCase = (str: string) => {
  if (!str) return ''
  return str.toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())
}

type Employee = {
  firstName?: string | null
  lastName?: string | null
  emailAddress?: string | null
}

export function formatEmployeeDisplay(employee?: Employee): string {
  if (!employee) return ''

  const first = employee.firstName ? toProperCase(employee.firstName) : ''
  const last = employee.lastName ? toProperCase(employee.lastName) : ''
  const email = employee.emailAddress ?? ''

  const name = `${first} ${last}`.trim()
  return name && email ? `${name} - ${email}` : name || email
}

// ✅ TypeScript (keeps original objects immutable)
type ExtraFields = {
  backupApproverEmail: string
  backupBeginDate: string | Date
  backupEndDate: string | Date
  assignmentKey?: string
  isNewlyAdded?: boolean
  roleTitle?: string
}

export function addFieldsToRows<T>(
  rows: T[] | undefined,
  formatedUserValue: string,
  beginDate: string | Date,
  endDate: string | Date
): (T & ExtraFields)[] {
  if (!Array.isArray(rows)) return []
  return rows.map((r) => ({
    ...r,
    id: uuidv4(),
    backupApproverEmail: formatedUserValue,
    backupBeginDate: beginDate,
    backupEndDate: endDate,
    assignmentKey: buildAssignmentKey(
      r,
      (r as any)?.roleTitle || '',
      formatedUserValue,
      beginDate,
      endDate
    ),
    isNewlyAdded: true,
    isActive: true,
  }))
}

export const buildAssignmentKey = (
  r: any,
  roleTitle: any,
  formatedUserValue: any,
  beginDate: any,
  endDate: any
) =>
  [
    roleTitle,
    r.markets,
    r.countryCodes,
    formatedUserValue,
    beginDate instanceof Date ? beginDate.toUTCString() : (beginDate ?? ''),
    endDate instanceof Date ? endDate.toUTCString() : (endDate ?? ''),
  ]
    .map((x) => (x ?? '').toString().trim())
    .join('|')

export function formatDateLocal(date: Date) {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
export const parseISODate = (str: string) => {
  const [yyyy, mm, dd] = str.split('-')
  return new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)))
}

export function tryToUtcIso(dateStr: string, endOfDay = false): string | null {
  if (!dateStr) return null
  let m = moment.utc(dateStr, moment.ISO_8601, true)
  if (!m.isValid()) {
    m = moment.utc(dateStr, 'DD/MM/YYYY', true)
  }
  if (!m.isValid()) {
    m = moment.utc(dateStr, 'YYYY-MM-DD', true)
  }
  if (m.isValid()) {
    if (endOfDay) {
      m.endOf('day')
    }
    return m.toISOString()
  }
  return null
}

export function tryToUtcIsoEndOfDay(dateStr: string): string | null {
  return tryToUtcIso(dateStr, true)
}

export function enrichBackupApprovers(backups: any[], approverGroups: any[]) {
  const groupsById = new Map(
    backups.map((g) => [g.groupId, { groupName: g.groupName, roleTitle: g.roleTitle }])
  )

  return approverGroups.map((b) => {
    const match = groupsById.get(b.approverGroupId)

    return {
      ...b,
      groupName: match?.groupName ?? null,
      roleTitle: match?.roleTitle ?? null,
      isNewlyAdded: false,
      assignmentKey: buildAssignmentKey(
        b,
        match?.roleTitle,
        b.backupApproverEmail,
        b.backupBeginDate,
        b.backupEndDate
      ),
    }
  })
}

export const formatDMY = (v?: string | Date) => (v ? moment.utc(v).format('DD/MM/YYYY') : '')

export const parseToIsoUtc = (v: any, oldValue: any) => {
  if (!v) return null
  const m = moment.utc(v, ['DD/MM/YYYY', moment.ISO_8601], true)
  return m.isValid() ? m.toISOString() : oldValue
}

export type SelectedBackupApproverGroup = {
  id?: string | number | null
  isNewlyAdded: boolean
  groupId?: number | string // used when isNewlyAdded = true
  approverGroupId?: number | string // used when isNewlyAdded = false
  backupApproverEmail: string
  region?: string | null
  markets?: string | null
  countryCodes?: string | null
  productLines: string
  backupBeginDate: string | Date
  backupEndDate: string | Date
  isActive: boolean
}

export function buildSelectedBackupApproverGroupsInput(
  selectedBackupApproverGroups: SelectedBackupApproverGroup[],
  userEmail: string
): any[] {
  return selectedBackupApproverGroups.map((x) => ({
    id: x.isNewlyAdded ? null : (x.id ?? null),
    workflowApproverEmail: userEmail,
    backupApproverEmail: x.backupApproverEmail,
    approverGroupId: x.isNewlyAdded ? x.groupId : x.approverGroupId,
    region: x.region ?? null,
    markets: x.markets ?? null,
    countryCodes: x.countryCodes ?? null,
    productLines: x.productLines ?? '*',
    backupBeginDate: x.isNewlyAdded ? tryToUtcIso(x.backupBeginDate as string) : x.backupBeginDate,
    backupEndDate: x.isNewlyAdded
      ? tryToUtcIsoEndOfDay(x.backupEndDate as string)
      : x.backupEndDate,
    isActive: x.isActive,
  }))
}
